import React, { createContext, useCallback, useContext, useMemo, useRef, useState } from "react";
import { Snackbar, Alert } from "@mui/material";
import { useEventStream, type NotificationEvent } from "./useEventStream";

export type ToastSeverity = "success" | "info" | "warning" | "error";

interface ToastState {
  open: boolean;
  message: string;
  severity: ToastSeverity;
}

interface NotificationsContextValue {
  unreadCount: number;
  markRead: () => void;
  /** Imperatively show a transient toast for a client-side action — a copy, a
   *  save, an install, a failure. This is the entry point for feedback that
   *  happens OUTSIDE a dialog; reach for it instead of leaving an action
   *  silent. SSE notification events also surface here automatically. */
  notify: (message: string, severity?: ToastSeverity) => void;
  /** Subscribe to every SSE notification event (the single shared stream).
   *  Returns an unsubscribe fn. Lets other features (e.g. the chat view
   *  reacting to a background-subagent job_event) react without opening a
   *  second EventSource. */
  subscribe: (handler: (ev: NotificationEvent) => void) => () => void;
  /** Test-only hook to inject an event without a live SSE connection. */
  pushForTest: (ev: NotificationEvent) => void;
}

const NotificationsContext = createContext<NotificationsContextValue | null>(null);

export function useNotifications(): NotificationsContextValue {
  const ctx = useContext(NotificationsContext);
  if (!ctx) throw new Error("useNotifications must be used inside <NotificationsProvider>");
  return ctx;
}

/** Non-throwing variant — returns null when there's no provider (e.g. a
 *  standalone ChatProvider in a test). Lets optional consumers degrade. */
export function useNotificationsOptional(): NotificationsContextValue | null {
  return useContext(NotificationsContext);
}

function describeEvent(ev: NotificationEvent): string {
  const title = typeof ev.payload.title === "string" ? ev.payload.title : ev.type;
  const status = typeof ev.payload.status === "string" ? ` — ${ev.payload.status}` : "";
  return `${title}${status}`;
}

export interface NotificationsProviderProps {
  /** Bearer for cookie-less contexts (embed); forwarded to the SSE stream
   *  as ?token=. Omit for the SPA (cookie auth). */
  token?: string | null;
  apiUrl: string;
  children: React.ReactNode;
}

export function NotificationsProvider({ apiUrl, token = null, children }: NotificationsProviderProps) {
  const [unreadCount, setUnreadCount] = useState(0);
  const [toast, setToast] = useState<ToastState>({ open: false, message: "", severity: "info" });

  const subscribersRef = useRef<Set<(ev: NotificationEvent) => void>>(new Set());

  const subscribe = useCallback(
    (handler: (ev: NotificationEvent) => void) => {
      subscribersRef.current.add(handler);
      return () => {
        subscribersRef.current.delete(handler);
      };
    },
    [],
  );

  // Imperative toast for client-side actions. Does NOT bump unreadCount — the
  // badge tracks server-side notifications, not local UI feedback.
  const notify = useCallback((message: string, severity: ToastSeverity = "info") => {
    setToast({ open: true, message, severity });
  }, []);

  const handleEvent = useCallback((ev: NotificationEvent) => {
    setUnreadCount((n) => n + 1);
    // Single-toast model: a burst of events shows only the latest message in
    // the Snackbar (unreadCount still counts each one). A queue can come later
    // if bursty job completions make this a problem.
    setToast({ open: true, message: describeEvent(ev), severity: "info" });
    // Fan out to subscribers (chat view, etc.). A throwing subscriber must not
    // break the toast or other subscribers.
    for (const sub of subscribersRef.current) {
      try {
        sub(ev);
      } catch {
        /* swallow */
      }
    }
  }, []);

  useEventStream({ apiUrl, onEvent: handleEvent, token });

  const value = useMemo<NotificationsContextValue>(
    () => ({
      unreadCount,
      markRead: () => setUnreadCount(0),
      notify,
      subscribe,
      pushForTest: handleEvent,
    }),
    [unreadCount, handleEvent, notify, subscribe],
  );

  return (
    <NotificationsContext.Provider value={value}>
      {children}
      <Snackbar
        open={toast.open}
        autoHideDuration={4000}
        onClose={() => setToast((t) => ({ ...t, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          severity={toast.severity}
          variant="filled"
          onClose={() => setToast((t) => ({ ...t, open: false }))}
          // role=status + polite live region: announced by screen readers
          // without stealing focus, the right register for transient feedback.
          role="status"
          sx={{ alignItems: "center", borderRadius: 2 }}
        >
          {toast.message}
        </Alert>
      </Snackbar>
    </NotificationsContext.Provider>
  );
}
